<template>
    <div>
        <h5 class="mb-4">
            <strong>Reglas de consejos de prudencia</strong>
        </h5>

        <b-row class="mb-3">
            <b-col md="3">
                <b-form-group label="Nombre">
                    <b-form-input v-model="filterRulesTipsPrudence.name" type="text" placeholder="Buscar por nombre"
                        class="form-control" />
                </b-form-group>
            </b-col>

            <b-col md="3">
                <b-form-group label="Estado">
                    <el-select v-model="filterRulesTipsPrudence.is_active" clearable filterable size="medium"
                        placeholder="Buscar por estado">
                        <el-option v-for="item in statesSga" :key="item.id" :label="item.name" :value="item.id" />
                    </el-select>
                </b-form-group>
            </b-col>

            <b-col md="3" class="mt-auto">
                <b-form-group>
                    <b-button variant="primary" @click="searchRulesTipsPrudenceSga">
                        <i class="fa fa-search"></i>
                        <span class="ml-1" v-text="'Buscar'"></span>
                    </b-button>
                    <b-button class="mx-1" @click="clearFilter">
                        <i class="fa fa-eraser"></i>
                        <span class="ml-1" v-text="'Limpiar'"></span>
                    </b-button>
                </b-form-group>
            </b-col>

            <b-col md="12" class="mt-4 d-flex justify-content-end align-items-center">
                <b-form-group>
                    <b-button v-if="canManageConfiguration" variant="primary" @click="createRuleTipPrudence">
                        <i class="fa fa-plus" />
                        <span class="ml-1" v-text="'Nueva regla de consejo'" />
                    </b-button>
                </b-form-group>
            </b-col>

            <b-modal id="modal_create_rules_tips_prudence" size="md" hide-footer no-close-on-esc no-close-on-backdrop
                hide-header-close>
                <template #modal-title>
                    {{ action === 'edit' ? 'Editar' : 'Registrar nueva' }} regla de consejo de prudencia
                </template>
                <formRulesTipsPrudence />
            </b-modal>

            <b-col md="12">
                <b-form-group>
                    <b-table :items="rulesTipsPrudence.data" :fields="fields" responsive striped hover bordered
                        thead-tr-class="table-active" show-empty thead-class="table-active"
                        empty-text="No hay resultados para mostrar.">   
                        
                        <!-- Versión del SGA -->
                        <template #cell(edition)="data">
                            <b-badge variant="info" class="text-white">
                                {{ data.item.version_sga && typeof data.item.version_sga === 'object' ? data.item.version_sga.version : 'No registra' }}
                            </b-badge>
                        </template>

                        <!-- COMBINACIÓN MEJORADA -->
                        <template #cell(prudence_tip_combination)="data">
                            <div class="combinations-container">
                                <!-- Header con contador -->
                                <div class="combinations-header">
                                    <span class="count-badge warning">{{ data.item.prudence_tip_combination.length }}</span>
                                    <span class="count-label">combinaciones</span>
                                </div>
                                
                                <!-- Lista de combinaciones mejorada -->
                                <div class="combinations-list">
                                    <div 
                                        v-for="combination in data.item.prudence_tip_combination"
                                        :key="combination.id"
                                        class="combination-item warning-item"
                                    >
                                        <div class="combination-content">
                                            <span class="code-tag warning-code">{{ combination.code }}</span>
                                            <span 
                                                class="description-text" 
                                                :title="combination.name"
                                                @mouseenter="showTooltip($event, combination.name)"
                                                @mouseleave="hideTooltip"
                                            >
                                                {{ truncateText(combination.name) }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>

                        <!-- A MOSTRAR MEJORADA -->
                        <template #cell(prudence_tip_display)="data">
                            <div class="combinations-container">
                                <!-- Header con contador -->
                                <div class="combinations-header">
                                    <span class="count-badge success">{{ data.item.prudence_tip_display.length }}</span>
                                    <span class="count-label">a mostrar</span>
                                </div>
                                
                                <!-- Lista de elementos a mostrar mejorada -->
                                <div class="combinations-list">
                                    <div 
                                        v-for="display in data.item.prudence_tip_display"
                                        :key="display.id"
                                        class="combination-item success-item"
                                    >
                                        <div class="combination-content">
                                            <span class="code-tag success-code">{{ display.code }}</span>
                                            <span 
                                                class="description-text" 
                                                :title="display.name"
                                                @mouseenter="showTooltip($event, display.name)"
                                                @mouseleave="hideTooltip"
                                            >
                                                {{ truncateText(display.name) }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>

                        <template #cell(is_active)="data">
                            <b-badge :variant="data.item.is_active ? 'success' : 'danger'"
                                v-text="data.item.is_active ? 'ACTIVO' : 'INACTIVO'" />
                        </template>

                        <template #cell(actions)="data">
                            <div class="d-flex justify-content-lg-center justify-content-between flex-wrap flex-nowrap">
                                <el-tooltip content="Editar" placement="top" effect="light">
                                    <el-button type="text" class="text-primary" @click="editRuleTipPrudence(data.item)">
                                        <i class="fa fa-edit fa-lg"></i>
                                    </el-button>
                                </el-tooltip>
                                <el-tooltip class="ml-2" :content="data.item.is_active ? 'Inactivar' : 'Activar'"
                                    placement="top" effect="light">
                                    <el-button type="text" @click="changeStatus(data.item.id)"
                                        :class="`${!data.item.is_active ? 'text-success' : 'text-danger'}`">
                                        <i :class="`fa fa-lg ${!data.item.is_active ? 'fa-check' : 'fa-close'}`"></i>
                                    </el-button>
                                </el-tooltip>
                            </div>
                        </template>
                    </b-table>
                </b-form-group>
            </b-col>

            <b-col md="12">
                <b-form-group>
                    <div class="d-flex justify-content-end align-items-center pr-4">
                        <el-pagination style="max-width: 100%" v-model="filterRulesTipsPrudence.page"
                            :total="filterRulesTipsPrudence.total" :current-page="filterRulesTipsPrudence.page"
                            :page-size="filterRulesTipsPrudence.per_page" @size-change="afterChangeSizePagination"
                            @current-change="afterChangePagePagination" :page-sizes="[10, 30, 50, 100]"
                            layout="total, sizes, prev, pager, next" />
                    </div>
                </b-form-group>
            </b-col>
        </b-row>

        <!-- Tooltip personalizado -->
        <div 
            v-if="tooltip.show" 
            class="custom-tooltip"
            :style="{ top: tooltip.y + 'px', left: tooltip.x + 'px' }"
        >
            {{ tooltip.text }}
        </div>
    </div>
</template>

<script>
import { mapActions, mapState, mapMutations } from 'vuex';
import formRulesTipsPrudence from './FormRulesTipsPrudence.vue';

export default {
    name: 'rulesTipsPrudence',
    components: {
        formRulesTipsPrudence,
    },
    data() {
        return {
            // Nuevo estado para tooltip
            tooltip: {
                show: false,
                text: '',
                x: 0,
                y: 0
            },
        };
    },

    computed: {
        ...mapState('sgaRulesTipsPrudence', ['rulesTipsPrudence', 'action', 'filterRulesTipsPrudence']),
        ...mapState('sgaVersions', ['statesSga', 'activeGlobalVersion', 'activeVersions']),
        canManageConfiguration() {
            const activeVersion = this.activeVersions && this.activeVersions.find(v => v.id === this.activeGlobalVersion);
            
            const isVersionLocked = (activeVersion && activeVersion.is_locked) || false;
            
            return !isVersionLocked;
        },
        fields() {
            const baseFields = [
                {
                    key: "edition",
                    label: "Edición",
                    sortable: true,
                    tdClass: "text-center",
                    thClass: "text-center",
                },
                {
                    key: "name",
                    label: "Nombre",
                    sortable: true,
                    tdClass: "text-center",
                    thClass: "text-center",
                    thStyle: { width: "200px", "min-width": "200px", "max-width": "200px" }
                },
                {
                    key: "prudence_tip_combination",
                    label: "Combinación",
                    sortable: true,
                    tdClass: "text-left",
                    thClass: "text-center",
                    thStyle: { width: "350px", "min-width": "350px", "max-width": "350px" }
                },
                {
                    key: "prudence_tip_display",
                    label: "A mostrar",
                    sortable: true,
                    tdClass: "text-left",
                    thClass: "text-center",
                    thStyle: { width: "350px", "min-width": "350px", "max-width": "350px" }
                },
                {
                    key: "is_active",
                    label: "Estado",
                    tdClass: "text-center",
                    thClass: "text-center",
                },
            ];

            if (this.canManageConfiguration) {
                baseFields.push({
                    key: "actions",
                    label: "Acciones",
                    sortable: false,
                    tdClass: "text-center",
                    thClass: "text-center",
                });
            }

            return baseFields;
        }
    },

    methods: {
        ...mapActions('sgaRulesTipsPrudence', ['getRulesTipsPrudenceSga', 'changeStatusRuleTipPrudenceSga', 'showRuleTipPrudenceSga']),
        ...mapMutations('sgaRulesTipsPrudence', ['setActionRuleTipPrudence', 'setDefaultFilterRulesTipsPrudence', 'loadAfterChangeSizePagination',
            'loadAfterChangePagePagination', 'setFilterRulesTipsPrudenceSga']),

        // NUEVOS MÉTODOS
        truncateText(text) {
            if (!text) return '';
            return text.length > 120 ? text.substring(0, 120) + '...' : text;
        },
        
        showTooltip(event, text) {
            if (text && text.length > 120) {
                this.tooltip = {
                    show: true,
                    text: text,
                    x: event.pageX + 10,
                    y: event.pageY + 10
                };
            }
        },
        
        hideTooltip() {
            this.tooltip.show = false;
        },

        // MÉTODOS EXISTENTES
        createRuleTipPrudence() {
            this.setActionRuleTipPrudence('create');
            this.$bvModal.show('modal_create_rules_tips_prudence');
        },

        async clearFilter() {
            this.setDefaultFilterRulesTipsPrudence();
            this.setFilterRulesTipsPrudenceSga({ ...this.filterRulesTipsPrudence, version_id: this.activeGlobalVersion });
            await this.getRulesTipsPrudenceSga();
        },

        async afterChangeSizePagination(size) {
            this.loadAfterChangeSizePagination(size);
            await this.getRulesTipsPrudenceSga();
        },

        async afterChangePagePagination(page) {
            this.loadAfterChangePagePagination(page);
            await this.getRulesTipsPrudenceSga();
        },

        async changeStatus(id) {
            await this.changeStatusRuleTipPrudenceSga(id);
            await this.getRulesTipsPrudenceSga();
        },

        async editRuleTipPrudence(ruleTipPrudence) {
            await this.showRuleTipPrudenceSga(ruleTipPrudence.id);
            this.setActionRuleTipPrudence('edit');
            this.$bvModal.show('modal_create_rules_tips_prudence');
        },

        async searchRulesTipsPrudenceSga() {
            await this.getRulesTipsPrudenceSga(true);
        },
    },

    watch: {
        activeGlobalVersion: {
            async handler(newVal) {
                this.setFilterRulesTipsPrudenceSga({ ...this.filterRulesTipsPrudence, version_id: newVal });
                await this.getRulesTipsPrudenceSga();
            },
            immediate: true,
            deep: true,
        },
    },
};
</script>

<style scoped>
/* Contenedor principal de las combinaciones - MÁS COMPACTO */
.combinations-container {
    max-width: 340px;
    margin: 0 auto;
}

/* Header con contador - MÁS COMPACTO */
.combinations-header {
    display: flex;
    align-items: center;
    margin-bottom: 6px;
    padding-bottom: 6px;
    border-bottom: 1px solid #dee2e6;
}

.count-badge {
    font-size: 0.7rem;
    font-weight: 600;
    padding: 0;
    border-radius: 50%;
    margin-right: 6px;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
}

.count-badge.warning {
    background-color: #ffc107;
    color: #212529;
}

.count-badge.success {
    background-color: #28a745;
    color: white;
}

.count-label {
    font-size: 0.75rem;
    color: #6c757d;
    font-weight: 500;
}

/* Lista de combinaciones - MÁS COMPACTA */
.combinations-list {
    max-height: 180px;
    overflow-y: auto;
}

/* Cada item de combinación - MÁS COMPACTO */
.combination-item {
    background-color: white;
    border: 1px solid #e9ecef;
    border-radius: 4px;
    margin-bottom: 4px;
    padding: 3px 6px;
    transition: all 0.2s ease;
}

.combination-item:last-child {
    margin-bottom: 0;
}

.combination-item:hover {
    box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}

.combination-item.warning-item {
    border-left: 3px solid #ffc107;
}

.combination-item.success-item {
    border-left: 3px solid #28a745;
}

/* Contenido de cada combinación - MÁS COMPACTO */
.combination-content {
    display: flex;
    align-items: flex-start;
    gap: 6px;
}

/* Tag del código - MÁS COMPACTO */
.code-tag {
    font-size: 0.65rem;
    font-weight: 700;
    padding: 2px 5px;
    border-radius: 3px;
    flex-shrink: 0;
    min-width: 40px;
    text-align: center;
}

.code-tag.warning-code {
    background-color: #ffc107;
    color: #212529;
}

.code-tag.success-code {
    background-color: #28a745;
    color: white;
}

/* Texto de descripción - TRUNCADO */
.description-text {
    font-size: 0.75rem;
    line-height: 1.25;
    color: #495057;
    flex-grow: 1;
    word-break: break-word;
    cursor: help;
}

/* Tooltip personalizado */
.custom-tooltip {
    position: fixed;
    background-color: #333;
    color: white;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 0.75rem;
    max-width: 300px;
    z-index: 1050;
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    word-wrap: break-word;
    line-height: 1.4;
    pointer-events: none;
    animation: fadeIn 0.2s ease-in-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(-5px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Scrollbar personalizada para la lista */
.combinations-list::-webkit-scrollbar {
    width: 3px;
}

.combinations-list::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 2px;
}

.combinations-list::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 2px;
}

.combinations-list::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}

/* Mejoras para la tabla */
.table td {
    vertical-align: middle;
    padding: 8px 6px;
}

.table-hover tbody tr:hover {
    background-color: rgba(0,123,255,0.02);
}

/* Responsive */
@media (max-width: 768px) {
    .combinations-container {
        max-width: none;
        margin: 0;
    }
    
    .combination-content {
        flex-direction: column;
        gap: 3px;
    }
    
    .code-tag {
        align-self: flex-start;
    }
}
</style>